/**
 * The partner programme as staff run it (`/partners/…`, `docs/30_PARTNER_PORTAL.md`).
 *
 * Amounts are the server's strings, formatted and never computed with. Every
 * action is checked on the server — the permission, the state the record is
 * in, and that one person never approves their own adjustment or both halves
 * of a payout; the screens only offer what is likely to be allowed.
 */

import { api, type Tone } from '@connect/web-shared';

import type { PayoutPlan } from './plan-editor';

export type Service = 'sms' | 'ai' | 'email' | 'rcs';

export const SERVICE_LABELS: Record<Service, string> = {
  sms: 'SMS credits',
  ai: 'AI assistant',
  email: 'Email',
  rcs: 'RCS',
};

export interface PartnerRow {
  public_id: string;
  reference: string;
  name: string;
  kind: 'agency' | 'reseller' | 'individual';
  status: 'applied' | 'active' | 'suspended' | 'terminated' | 'rejected';
  tier: string | null;
  contact_name: string;
  email: string;
  phone: string | null;
  website: string | null;
  city: string | null;
  state: string | null;
  approved_at: string | null;
  applied_at?: string | null;
  active_customers?: number;
}

export interface Rule {
  service: Service;
  rate: string;
  max_discount_rate: string;
  window_months: number | null;
  unbounded: boolean;
  cap_per_customer: string | null;
}

export interface PartnerUser {
  public_id: string;
  name: string;
  email: string;
  mobile: string | null;
  role: 'owner' | 'manager' | 'staff';
  status: 'active' | 'disabled';
  must_change_password: boolean;
  last_sign_in: string | null;
}

export interface PayoutDetails {
  legal_name: string | null;
  pan: string | null;
  gst_registered: boolean;
  gstin: string | null;
  bank_holder: string | null;
  bank_account: string | null;
  bank_ifsc: string | null;
  bank_name: string | null;
  bank_branch: string | null;
  bank_city: string | null;
  bank_state: string | null;
  status: 'none' | 'unverified' | 'verified' | 'correction_requested';
  submitted_at: string | null;
  verified_at: string | null;
  correction_reason: string | null;
  /** `false`: inoperative — taxed as no PAN. `null`: not known. */
  pan_operative: boolean | null;
}

export interface Money {
  eligible_revenue: string;
  pool: string;
  discounts_given: string;
  gross: string;
  reversals: string;
  adjustments: string;
  estimated: string;
  payable: string;
  in_payout: string;
  paid: string;
  tds_deducted: string;
  net_paid: string;
}

export interface PartnerDetail {
  partner: PartnerRow;
  application: { message?: string };
  status_reason: string | null;
  linked_client: { public_id: string; name: string } | null;
  payout_details: PayoutDetails;
  users: PartnerUser[];
  terms: { starts_at: string; ends_at: string | null; version: number; rules: Rule[] } | null;
  agreements: {
    public_id: string;
    policy: string;
    version: number;
    starts_at: string;
    ends_at: string | null;
    note: string;
  }[];
  customers: { registrations: number; awaiting_review: number; active_customers: number };
  money: Money;
}

export interface RegistrationRow {
  public_id: string;
  reference: string;
  partner: string;
  company_name: string;
  contact_name: string;
  email: string | null;
  phone: string | null;
  tax_id: string | null;
  city: string | null;
  services: Service[];
  notes: string;
  status: 'submitted' | 'duplicate_review' | 'approved' | 'rejected' | 'withdrawn';
  match_reasons: string[];
  existing_customer: boolean;
  decision_note: string | null;
  submitted_at: string;
  decided_at: string | null;
}

export interface RegistrationDetail {
  registration: RegistrationRow;
  matched_client: { public_id: string; name: string; credited_to: string | null } | null;
  events: { kind: string; detail: Record<string, unknown>; by: string; at: string }[];
}

export interface AttributionRow {
  public_id: string;
  partner: string;
  client: string;
  status: 'active' | 'ended';
  source: string;
  since: string;
  until: string | null;
  ended_reason: string | null;
}

export interface Policy {
  public_id: string;
  name: string;
  description: string;
  versions: {
    public_id: string;
    version: number;
    status: 'draft' | 'published' | 'retired';
    note: string;
    published_at: string | null;
    rules: Rule[];
  }[];
}

export interface LedgerRow {
  public_id: string;
  reference: string;
  client: string | null;
  service: Service | null;
  kind: 'accrual' | 'reversal' | 'adjustment' | 'tds' | 'gst';
  business_day: string | null;
  eligible_amount: string;
  rate: string | null;
  pool: string;
  discount_amount: string;
  gross: string;
  amount: string;
  status: string;
  matures_at: string;
  description: string;
  at: string;
  inputs?: Record<string, unknown>;
}

export interface Adjustment {
  public_id: string;
  partner: string;
  amount: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  requested_by_me: boolean;
  decision_note: string | null;
  at: string;
}

export interface PayoutRow {
  public_id: string;
  reference: string;
  partner: string;
  status:
    | 'requested'
    | 'under_review'
    | 'approved'
    | 'processing'
    | 'paid'
    | 'rejected'
    | 'failed'
    | 'cancelled';
  trigger: 'scheduled' | 'request';
  scheduled_for: string | null;
  gross: string;
  tds: string;
  gst: string;
  net: string;
  bank: { holder: string | null; account: string | null; ifsc: string | null } | null;
  payment_reference: string | null;
  paid_on: string | null;
  note: string | null;
  needs_second_approval: boolean;
  approved_at: string | null;
  second_approved_at: string | null;
  requested_at: string;
  finished_at: string | null;
}

export interface TaxRule {
  public_id: string;
  kind: 'tds' | 'gst';
  rate: string;
  /** TDS: the rate for a partner who has given no PAN. */
  rate_without_pan: string | null;
  annual_threshold: string | null;
  section: string;
  effective_from: string;
  note: string;
  adviser_reviewed: boolean;
  created_at: string;
}

export interface ProgramRules {
  plan: PayoutPlan;
  calendar: { weekend: 'indian_banks' | 'saturday_sunday' | 'sunday'; holidays: string[] };
  notice_bank_days: number;
  notice_to: string[];
  second_approval_over: string | null;
  settle_minutes: number;
}

export interface PartnerPayoutPlan extends PayoutPlan {
  own: boolean;
  describe: string;
  next_payout: string | null;
  on_hold: boolean;
  hold_reason: string | null;
  programme_plan: string;
}

export interface PayoutRuns {
  programme_plan: string;
  notice_bank_days: number;
  notice_to: string[];
  upcoming: { payout_on: string; prepared_on: string }[];
  runs: {
    public_id: string;
    reference: string;
    payout_on: string;
    payouts: number;
    gross: string;
    notified_at: string | null;
    notice_error: string | null;
    bank_file_downloads: number;
    prepared_at: string;
  }[];
}

export interface Reconciliation {
  wallet_entries_not_yet_read: number;
  settle_minutes: number;
  ledger_entries_not_matching_sources: number;
  payouts_not_adding_up: string[];
  payouts_open_over_a_week: string[];
  open_fraud_alerts: number;
}

export interface Alert {
  public_id: string;
  kind: string;
  partner: string | null;
  client: string | null;
  detail: Record<string, unknown>;
  status: 'open' | 'closed';
  note: string | null;
  at: string;
}

export const partnerKeys = {
  all: ['partners'] as const,
  list: ['partners', 'list'] as const,
  detail: (id: string) => ['partners', 'detail', id] as const,
  registrations: ['partners', 'registrations'] as const,
  registration: (id: string) => ['partners', 'registration', id] as const,
  attributions: ['partners', 'attributions'] as const,
  disputes: ['partners', 'disputes'] as const,
  policies: ['partners', 'policies'] as const,
  ledger: (partner?: string) => ['partners', 'ledger', partner ?? 'all'] as const,
  adjustments: ['partners', 'adjustments'] as const,
  reconciliation: ['partners', 'reconciliation'] as const,
  codes: ['partners', 'codes'] as const,
  alerts: ['partners', 'alerts'] as const,
  program: ['partners', 'program'] as const,
  plan: (id: string) => ['partners', 'plan', id] as const,
  runs: ['partners', 'runs'] as const,
  payouts: ['partners', 'payouts'] as const,
  payout: (id: string) => ['partners', 'payout', id] as const,
  tax: ['partners', 'tax'] as const,
};

export const partnersApi = {
  create: (body: Record<string, unknown>) => api.post<PartnerRow>('/partners', body),
  detail: (id: string) => api.get<PartnerDetail>(`/partners/${id}`),
  update: (id: string, body: Record<string, unknown>) => api.patch<PartnerRow>(`/partners/${id}`, body),
  approve: (id: string, body: Record<string, unknown>) =>
    api.post<PartnerRow & { first_password: string | null }>(`/partners/${id}/approve`, body),
  status: (id: string, action: 'reject' | 'suspend' | 'reinstate' | 'terminate', reason: string) =>
    api.post<PartnerRow>(`/partners/${id}/status/${action}`, { reason }),
  startAgreement: (id: string, body: Record<string, unknown>) => api.post(`/partners/${id}/agreements`, body),
  endAgreement: (id: string, reason: string) => api.post(`/partners/agreements/${id}/end`, { reason }),
  resetPassword: (userId: string) =>
    api.post<PartnerUser & { first_password: string | null }>(`/partners/users/${userId}/password`, {}),
  setUserActive: (userId: string, active: boolean) =>
    api.post<PartnerUser>(`/partners/users/${userId}/active`, undefined, { params: { active: String(active) } }),
  registration: (id: string) => api.get<RegistrationDetail>(`/partners/registrations/${id}`),
  decide: (id: string, body: Record<string, unknown>) =>
    api.post<RegistrationRow>(`/partners/registrations/${id}/decision`, body),
  reassign: (id: string, partner: string, reason: string) =>
    api.post(`/partners/attributions/${id}/reassign`, { partner, reason }),
  endAttribution: (id: string, reason: string) => api.post(`/partners/attributions/${id}/end`, { reason }),
  disputes: (state?: string) =>
    api.get<{ public_id: string; partner: string; reason: string; status: string; resolution: string | null; at: string }[]>(
      '/partners/disputes',
      { params: state ? { state } : {} },
    ),
  resolveDispute: (id: string, body: Record<string, unknown>) => api.post(`/partners/disputes/${id}/resolve`, body),
  policies: () => api.get<Policy[]>('/partners/policies'),
  createPolicy: (body: Record<string, unknown>) => api.post<{ public_id: string }>('/partners/policies', body),
  createVersion: (policy: string, body: Record<string, unknown>) =>
    api.post<{ public_id: string }>(`/partners/policies/${policy}/versions`, body),
  replaceVersion: (version: string, body: Record<string, unknown>) => api.put(`/partners/versions/${version}`, body),
  publish: (version: string) => api.post(`/partners/versions/${version}/publish`),
  retire: (version: string) => api.post(`/partners/versions/${version}/retire`),
  revokeCode: (id: string, reason: string) => api.post(`/partners/codes/${id}/revoke`, { reason }),
  runAccrual: () => api.post<{ read: number; earned: number; entries: number }>('/partners/ledger/run'),
  reconciliation: () => api.get<Reconciliation>('/partners/reconciliation'),
  adjustments: (state?: string) => api.get<Adjustment[]>('/partners/adjustments', { params: state ? { state } : {} }),
  createAdjustment: (partner: string, body: Record<string, unknown>) =>
    api.post(`/partners/${partner}/adjustments`, body),
  decideAdjustment: (id: string, approve: boolean, note: string) =>
    api.post(`/partners/adjustments/${id}/decision`, { approve, note }),
  payout: (id: string) =>
    api.get<{ payout: PayoutRow; payout_details: PayoutDetails; entries: LedgerRow[] }>(`/partners/payouts/${id}`),
  payoutAction: (id: string, action: 'review' | 'approve' | 'processing') =>
    api.post<PayoutRow>(`/partners/payouts/${id}/${action}`),
  rejectPayout: (id: string, reason: string) => api.post<PayoutRow>(`/partners/payouts/${id}/reject`, { reason }),
  failPayout: (id: string, reason: string) => api.post<PayoutRow>(`/partners/payouts/${id}/failed`, { reason }),
  paid: (id: string, body: Record<string, unknown>) => api.post<PayoutRow>(`/partners/payouts/${id}/paid`, body),
  payoutPlan: (id: string) => api.get<PartnerPayoutPlan>(`/partners/${id}/payout-plan`),
  setPayoutPlan: (id: string, body: Record<string, unknown>) =>
    api.put<PartnerPayoutPlan>(`/partners/${id}/payout-plan`, body),
  runs: () => api.get<PayoutRuns>('/partners/payout-runs'),
  prepareRuns: () =>
    api.post<{ prepared: string[]; passed_over: { partner: string; date: string; reason: string }[]; told: number }>(
      '/partners/payout-runs/prepare',
    ),
  resendNotice: (id: string) => api.post<{ told: number; notice_error: string | null }>(`/partners/payout-runs/${id}/notice`),
  bankFile: (id: string) => api.download(`/partners/payout-runs/${id}/bank-file`, 'partner-bank-transfers.xlsx'),
  taxRules: () => api.get<TaxRule[]>('/partners/tax-rules'),
  createTaxRule: (body: Record<string, unknown>) => api.post<TaxRule>('/partners/tax-rules', body),
  program: () => api.get<ProgramRules>('/partners/program'),
  updateProgram: (body: ProgramRules) => api.put<ProgramRules>('/partners/program', body),
  alerts: (state?: string) => api.get<Alert[]>('/partners/alerts', { params: state ? { state } : {} }),
  closeAlert: (id: string, note: string) => api.post(`/partners/alerts/${id}/close`, { note }),
};

export const PARTNER_STATUS: Record<PartnerRow['status'], { label: string; tone: Tone }> = {
  applied: { label: 'Applied', tone: 'info' },
  active: { label: 'Active', tone: 'success' },
  suspended: { label: 'Suspended', tone: 'warning' },
  terminated: { label: 'Terminated', tone: 'neutral' },
  rejected: { label: 'Rejected', tone: 'danger' },
};

export const REGISTRATION_STATUS: Record<RegistrationRow['status'], { label: string; tone: Tone }> = {
  submitted: { label: 'To review', tone: 'info' },
  duplicate_review: { label: 'Possible duplicate', tone: 'warning' },
  approved: { label: 'Approved', tone: 'success' },
  rejected: { label: 'Rejected', tone: 'danger' },
  withdrawn: { label: 'Withdrawn', tone: 'neutral' },
};

export const PAYOUT_STATUS: Record<PayoutRow['status'], { label: string; tone: Tone }> = {
  requested: { label: 'Requested', tone: 'info' },
  under_review: { label: 'Under review', tone: 'info' },
  approved: { label: 'Approved', tone: 'accent' },
  processing: { label: 'Processing', tone: 'accent' },
  paid: { label: 'Paid', tone: 'success' },
  rejected: { label: 'Rejected', tone: 'danger' },
  failed: { label: 'Failed', tone: 'danger' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};

export const LINE_KIND_LABELS: Record<LedgerRow['kind'], string> = {
  accrual: 'Commission',
  reversal: 'Given back',
  adjustment: 'Adjustment',
  tds: 'TDS',
  gst: 'GST',
};

export function labelsOf(map: Record<string, { label: string; tone: Tone }>) {
  return {
    labels: Object.fromEntries(Object.entries(map).map(([k, v]) => [k, v.label])),
    tones: Object.fromEntries(Object.entries(map).map(([k, v]) => [k, v.tone])),
  };
}

export function percent(rate: string | null | undefined): string {
  return rate == null || rate === '' ? '—' : `${rate}%`;
}

export function windowText(rule: Pick<Rule, 'window_months' | 'unbounded'>): string {
  if (rule.unbounded) return 'Unbounded (explicit)';
  return `${rule.window_months} month${rule.window_months === 1 ? '' : 's'}`;
}
